import { useState } from 'react';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../firebase';
import { useTranslation } from '../hooks/useTranslation';
import styles from './AuthModal.module.css';

function AuthModal({ onClose }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailForResend, setEmailForResend] = useState(null);
  const [passwordForResend, setPasswordForResend] = useState(null);
  const [isResetMode, setIsResetMode] = useState(false);
  const { signup, login, resendVerificationEmail, resetPassword } = useAuth();
  const { t } = useTranslation();

  const validateSignupForm = async () => {
    // Validation du format du pseudo
    const pseudoRegex = /^[a-zA-Z0-9._-]+$/;
    if (!displayName.trim()) {
      return t('auth.usernameRequired');
    }
    if (!pseudoRegex.test(displayName.trim())) {
      return t('auth.usernameInvalid');
    }

    // Validation du format de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return t('auth.emailInvalid');
    }

    // Validation de la longueur du mot de passe
    if (password.length < 6) {
      return t('auth.passwordMinLength');
    }

    // Validation de la correspondance des mots de passe
    if (password !== confirmPassword) {
      return t('auth.passwordsDontMatch');
    }

    // Vérifier l'unicité du pseudo
    const usersQuery = query(
      collection(db, 'users'),
      where('displayName', '==', displayName.trim())
    );
    const usersSnapshot = await getDocs(usersQuery);
    if (!usersSnapshot.empty) {
      return t('auth.usernameTaken');
    }

    // Vérifier l'unicité de l'email dans Firebase Auth
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email.trim().toLowerCase());
      if (signInMethods && signInMethods.length > 0) {
        return t('auth.emailTaken');
      }
    } catch (err) {
      // Non bloquant si erreur
    }

    // Vérifier l'unicité de l'email dans Firestore
    const emailQuery = query(
      collection(db, 'users'),
      where('email', '==', email.trim().toLowerCase())
    );
    const emailSnapshot = await getDocs(emailQuery);
    if (!emailSnapshot.empty) {
      return t('auth.emailTaken');
    }

    return null; // Pas d'erreur
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isResetMode) {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
          setError(t('auth.pleaseEnterEmail'));
          setLoading(false);
          return;
        }

        await resetPassword(trimmedEmail);
        setError(t('auth.resetEmailSent'));
        setIsResetMode(false);
        setLoading(false);
        return;
      }

      if (isLogin) {
        await login(email.trim(), password);
      } else {
        // Valider le formulaire d'inscription
        const validationError = await validateSignupForm();
        if (validationError) {
          setError(validationError);
          setLoading(false);
          return;
        }

        const trimmedEmail = email.trim();
        await signup(trimmedEmail, password, displayName);
      }
      onClose();
    } catch (err) {
      const isNetworkError =
        err.code === 'auth/network-request-failed' ||
        err.code === -26 ||
        err.code === '-26' ||
        (err.message && (String(err.message).includes('network') || String(err.message).includes('-26')));
      setError(isNetworkError ? t('auth.networkError') : (err.message || t('auth.errorOccurred')));
      // Si l'erreur concerne l'email non vérifié, stocker les identifiants pour pouvoir renvoyer l'email
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setEmailForResend(err.email);
        setPasswordForResend(password);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!emailForResend || !passwordForResend) return;
    
    setResendingEmail(true);
    setError('');
    
    try {
      await resendVerificationEmail(emailForResend, passwordForResend);
      setError(t('auth.verificationEmailResent'));
      setEmailForResend(null);
      setPasswordForResend(null);
    } catch (err) {
      setError(err.message || t('auth.verificationEmailError'));
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          ✕
        </button>
        <h2>
          {isResetMode ? t('auth.resetPassword') : isLogin ? t('auth.login') : t('auth.signup')}
        </h2>
        {error && (
          <div className={styles.error}>
            {error}
            {emailForResend && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  padding: 0, 
                  marginTop: '8px',
                  cursor: resendingEmail ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  color: 'inherit',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  display: 'block',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {resendingEmail ? t('auth.sending') : t('auth.resendLink')}
              </button>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {!isLogin && !isResetMode && (
            <div className={styles.formGroup}>
              <label htmlFor="displayName">{t('auth.username')}</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.toLowerCase())}
                placeholder={t('auth.usernamePlaceholder')}
                required={!isLogin}
                autoComplete="off"
              />
            </div>
          )}
          <div className={styles.formGroup}>
            <label htmlFor="identifier">
              {isResetMode ? t('auth.email') : isLogin ? t('auth.emailOrUsername') : t('auth.email')}
            </label>
            <input
              id="identifier"
              type={isResetMode ? 'email' : isLogin ? 'text' : 'email'}
              name={isResetMode ? 'email' : isLogin ? 'identifier' : 'email'}
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              placeholder={
                isResetMode
                  ? t('auth.emailPlaceholder')
                  : isLogin
                    ? t('auth.emailOrUsernamePlaceholder')
                    : t('auth.emailPlaceholder')
              }
              required
              autoComplete={isResetMode ? 'email' : isLogin ? 'username' : 'email'}
              data-google-autocomplete="false"
              data-autocomplete="off"
            />
          </div>

          {!isResetMode && (
            <div className={styles.formGroup}>
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={!isLogin ? t('auth.passwordPlaceholder') : ''}
                required
                minLength={6}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
              />
            </div>
          )}

          {isLogin && !isResetMode && (
            <button
              type="button"
              className={styles.switchBtn}
              onClick={() => {
                setIsResetMode(true);
                setError('');
                setEmail('');
                setPassword('');
                setConfirmPassword('');
              }}
            >
              {t('auth.forgotPassword')}
            </button>
          )}

          {!isLogin && !isResetMode && (
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                required={!isLogin}
                minLength={6}
                autoComplete="new-password"
              />
            </div>
          )}
          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading
              ? t('auth.loading')
              : isResetMode
                ? t('auth.sendLink')
                : isLogin
                  ? t('auth.connect')
                  : t('auth.signupButton')}
          </button>
        </form>
        {!isResetMode && (
          <button
            type="button"
            className={styles.switchBtn}
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
            }}
          >
            {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
          </button>
        )}
      </div>
    </div>
  );
}

export default AuthModal;

